import React, { useState } from "react";
import {
  BiBriefcase,
  BiChevronDown,
  BiChevronUp,
  BiMapPin,
  BiUser,
} from "react-icons/bi";
import GeneralModal from "../../component/Modals/General.modal";
import {
  getEducation,
  getApplication,
  getExperience,
  getGuarantor,
} from "./data";
import EducationView from "./EducationView";
import ExperienceView from "./ExperienceView";
import ApplicationView from "./application";
import GuarantorView from "./GuarantorView";

const CandidateCard = ({ seeker }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [activeSelection, setActiveSelection] = useState("");

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const [showNodal, setShowModal] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleShow = (selection) => {
    setShowModal(true);
    setActiveSelection(selection);
  };

  const renderView = () => {
    if (activeSelection === "education") {
      return <EducationView data={getEducation(seeker)} />;
    }
    if (activeSelection === "application") {
      return <ApplicationView data={getApplication(seeker)} />;
    }
    if (activeSelection === "experience") {
      return <ExperienceView data={getExperience(seeker)} />;
    }
    if (activeSelection === "guarantor") {
      return <GuarantorView data={getGuarantor(seeker)} />;
    }
  };

  return (
    <div className="bg-white sm:w-2/5 w-full mx-auto rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
      {/* Card Header */}
      <div className="p-6 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 ring-2 ring-indigo-100">
            {!imageError && seeker?.avatar ? (
              <img
                src={seeker.avatar}
                alt={`${seeker?.name || "Job seeker"} profile`}
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                <BiUser size={32} />
              </div>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-xl text-gray-900">
              {`${seeker?.firstName} ${seeker?.lastName}` ||
                "Anonymous Candidate"}
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              {seeker?.interestedJob?.join(", ") ||
                "No job interests specified"}
            </p>
          </div>
        </div>
        <button
          onClick={() => toggleExpand(seeker?.id)}
          className="p-2 rounded-full hover:bg-indigo-50 transition-colors duration-200"
        >
          {expandedId === seeker?.id ? (
            <BiChevronUp className="h-6 w-6 text-indigo-600" />
          ) : (
            <BiChevronDown className="h-6 w-6 text-indigo-600" />
          )}
        </button>
      </div>

      {/* Card Summary */}
      <div className="p-6 bg-gray-50">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center">
            <BiBriefcase className="h-5 w-5 text-indigo-500 mr-3" />
            <span className="text-sm text-gray-700">
              {seeker?.jobChoice?.join(", ") || "Not specified"}
            </span>
          </div>
          <div className="flex items-center">
            <BiMapPin className="h-5 w-5 text-indigo-500 mr-3" />
            <span className="text-sm text-gray-700">
              {seeker?.state || "Location not specified"}
            </span>
          </div>
          <div className="flex items-center">
            <BiUser className="h-5 w-5 text-indigo-500 mr-3" />
            <span className="text-sm text-gray-700">
              {seeker?.workExperience?.length || "Experience not specified"}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expandedId === seeker?.id && (
        <div className="p-6 border-t border-gray-100 transition-all duration-300 ease-in-out">
          <div className="space-y-5">
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Skills</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {seeker?.skills && seeker.skills.length > 0 ? (
                    seeker.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">
                      No skills listed
                    </span>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">Education</h4>
                <div>
                  {seeker?.education && seeker.education.length > 0 ? (
                    <button
                      onClick={() => handleShow("education")}
                      className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1 hover:bg-neutral-300 bg-neutral-100 rounded-full w-7 h-7 p-2 flex items-center justify-center"
                    >
                      {seeker.education.length}
                    </button>
                  ) : (
                    <p className="text-sm text-neutral-400">
                      Education details not provided
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">
                  Applications
                </h4>
                <div>
                  {seeker?.appliedJobs && seeker.appliedJobs.length > 0 ? (
                    <button
                      onClick={() => handleShow("application")}
                      className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1 hover:bg-neutral-300 bg-neutral-100 rounded-full w-7 h-7 p-2 flex items-center justify-center"
                    >
                      {seeker.appliedJobs.length}
                    </button>
                  ) : (
                    <p className="text-sm text-neutral-400">
                      No applications submitted
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">
                  Blacklist Status
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {seeker?.isBlacklisted ? "Blacklisted" : "Not blacklisted"}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">
                  Experience
                </h4>
                {seeker?.workExperience && seeker.workExperience.length > 0 ? (
                  <button
                    onClick={() => handleShow("experience")}
                    className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1 hover:bg-neutral-300 bg-neutral-100 rounded-full w-7 h-7 p-2 flex items-center justify-center"
                  >
                    {seeker.workExperience.length}
                  </button>
                ) : (
                  <p className="text-sm text-neutral-400">
                    No work experience listed
                  </p>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">Guarantor</h4>
                {seeker?.guarantors && seeker.guarantors.length > 0 ? (
                  <button
                    onClick={() => handleShow("guarantor")}
                    className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1 hover:bg-neutral-300 bg-neutral-100 rounded-full w-7 h-7 p-2 flex items-center justify-center"
                  >
                    {seeker.guarantors.length}
                  </button>
                ) : (
                  <p className="text-sm text-neutral-400">
                    No guarantors listed
                  </p>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700">Contact</h4>
                <div className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1">
                  <p>Email: {seeker?.email || "Email not available"}</p>
                </div>
                <div className="text-sm cursor-pointer hover:text-purple-600 text-gray-600 mt-1">
                  <p>
                    Phone: {seeker?.phoneNumber || "Phone number not available"}
                  </p>
                </div>
              </div>
            </div>

            <button className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 text-sm font-medium">
              Contact Job Seeker
            </button>
          </div>
        </div>
      )}
      <GeneralModal
        handleClose={() => setShowModal(false)}
        showCloseButton={true}
        width="lg"
        open={showNodal}
      >
        {renderView()}
      </GeneralModal>
    </div>
  );
};

export default CandidateCard;
